/**
 * CR320: S1.2 Diagnostics — Current Operation Section
 * 
 * Shows what's happening right now: page, stage, elapsed, attempt, last error/event.
 */

import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { S12RunData, S12DebugEvent } from "@/hooks/admin/useS12DiagnosticsData";

interface S12CurrentOpSectionProps {
  runData: S12RunData;
  events: S12DebugEvent[];
}

export function S12CurrentOpSection({ runData, events }: S12CurrentOpSectionProps) {
  const s12 = runData.discovery_debug?.s12;
  const lastEvent = events.length > 0 ? events[events.length - 1] : null;

  const stage = s12?.stage || runData.current_phase || "—";
  const page = s12?.page ?? runData.current_page ?? "—";
  const attempt = s12?.attempt ?? 1;
  const stageStartedAt = s12?.stage_started_at;
  const lastError = s12?.last_error;

  const stageElapsed = stageStartedAt
    ? formatDistanceToNow(new Date(stageStartedAt), { addSuffix: false })
    : "—";
  const status = String(runData.status ?? "").toLowerCase();
  const isActive = status === "running" || status === "in_progress";

  if (!isActive) {
    return (
      <div className="text-xs text-muted-foreground italic">
        Not currently running.
      </div>
    );
  }

  return (
    <div className="space-y-2 text-xs">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Page:</span>
          <span className="font-mono font-medium">{page}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Stage:</span>
          <Badge variant="outline" className="text-[10px] font-mono">{stage}</Badge>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Stage elapsed:</span>
          <span className="font-mono">{stageElapsed}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-muted-foreground">Attempt #:</span>
          <span className="font-mono">{attempt}</span>
        </div>
      </div>

      {/* Timings (from s12 debug) */}
      {s12?.timings_ms && (
        <div className="flex flex-wrap gap-1.5">
          {s12.timings_ms.fetch != null && (
            <Badge variant="outline" className="text-[10px] font-mono">
              fetch: {s12.timings_ms.fetch}ms
            </Badge>
          )}
          {s12.timings_ms.ocr != null && (
            <Badge variant="outline" className="text-[10px] font-mono">
              ocr: {s12.timings_ms.ocr}ms
            </Badge>
          )}
          {s12.timings_ms.score != null && (
            <Badge variant="outline" className="text-[10px] font-mono">
              score: {s12.timings_ms.score}ms
            </Badge>
          )}
        </div>
      )}

      {/* Last error */}
      {lastError && (
        <div className="p-2 bg-destructive/10 border border-destructive/20 rounded-md space-y-0.5">
          <div className="flex items-center gap-1.5 text-destructive">
            <span className="font-medium">Last error:</span>
            <code className="font-mono">{lastError.code || "unknown"}</code>
            {lastError.http_status && (
              <Badge variant="destructive" className="text-[10px]">{lastError.http_status}</Badge>
            )}
          </div>
          {lastError.msg && (
            <p className="text-muted-foreground truncate">{lastError.msg}</p>
          )}
        </div>
      )}

      {/* Last event */}
      {lastEvent && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>
            {lastEvent.stage && <span className="font-mono">[{lastEvent.stage}]</span>}{" "}
            {lastEvent.message}
            {lastEvent.ms != null && <span className="ml-1 font-mono">({lastEvent.ms}ms)</span>}
          </span>
        </div>
      )}
    </div>
  );
}
